import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Tooltip, Typography } from '@mui/material';
import { mdiInformation } from '@mdi/js';
import Icon from '@mdi/react';
import { ReadOnlyTypography } from './ReadOnlyTypography';
import { CopyButton } from './CopyButton';

interface LabelValueCommonProps {
	label: string;
	style?: React.CSSProperties;
	tooltip?: string;
	wrap?: boolean;
}

interface LabelValueCopyableProps {
	label: string;
	value: string;
	isCopyable: true;
}

interface LabelValueNotCopyableProps {
	label: string;
	value: string | React.ReactNode;
	isCopyable?: false;
}

type LabelValueProps = LabelValueCommonProps & (LabelValueCopyableProps | LabelValueNotCopyableProps);

export function LabelValue(props: LabelValueProps) {
	const { isCopyable, style, label, value, tooltip, wrap } = props;

	const labelRef = useRef<HTMLSpanElement>(null);
	const valueRef = useRef<HTMLSpanElement>(null);
	const [isLabelTruncated, setIsLabelTruncated] = useState(false);
	const [isValueTruncated, setIsValueTruncated] = useState(false);

	useEffect(() => {
		const el = labelRef.current;
		if (!el) return undefined;
		const check = () => { setIsLabelTruncated(el.scrollWidth > el.offsetWidth); };
		const observer = new ResizeObserver(check);
		observer.observe(el);
		check();
		return () => { observer.disconnect(); };
	}, [label]);

	useEffect(() => {
		const el = valueRef.current;
		if (!el) return undefined;
		const check = () => { setIsValueTruncated(el.scrollWidth > el.offsetWidth); };
		const observer = new ResizeObserver(check);
		observer.observe(el);
		check();
		return () => { observer.disconnect(); };
	}, [value]);

	const handleCopy = (e: any) => {
		e.stopPropagation();
		if (!isCopyable) return;
		navigator.clipboard.writeText(value).catch();
	};

	return (
		<Wrapper isCopyable={isCopyable} style={style}>
			<HeaderTypography aria-label={`ValueLabel-Label-${label}`} variant="buttonMediumMedium">
				<Tooltip title={label} placement="top" disableHoverListener={!isLabelTruncated} aria-label={`Tooltip-Label-${label}`}>
					<LabelText ref={labelRef}>{label}</LabelText>
				</Tooltip>
				{tooltip && (
					<Tooltip title={tooltip} placement="right">
						<IconWrapper aria-label={`Icon-${label}`}>
							<Icon path={mdiInformation} size="16px" color="rgba(0, 0, 0, 0.5)" />
						</IconWrapper>
					</Tooltip>
				)}
			</HeaderTypography>
			<Tooltip title={value} placement="top" disableHoverListener={!isValueTruncated} aria-label={`Tooltip-Value-${label}`}>
				<ValueContainer>
					{isCopyable && <CopyButton onClick={handleCopy} ariaLabel={`ValueLabel-CopyButton-${label}`} />}
					<ReadOnlyTypography
						ref={valueRef}
						aria-label={`ValueLabel-Value-${label}`}
						style={{
							userSelect: isCopyable ? 'all' : undefined,
							...(wrap && { overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }),
						}}
						noWrap={!wrap}
					>
						{value}
					</ReadOnlyTypography>
				</ValueContainer>
			</Tooltip>
		</Wrapper>
	);
}

const ValueContainer = styled.div`
	align-items: center;
	display: inline-flex;
	position: relative;
`;

const HeaderTypography = styled(Typography)(
	() => css`
		align-items: center;
		display: flex;
		gap: 8px;
		min-height: 28px !important;
		min-width: 0;
	`,
);

const LabelText = styled.span`
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const IconWrapper = styled.span`
	align-items: center;
	cursor: default;
	display: inline-flex;
	flex-shrink: 0;
`;

const Wrapper = styled.div<{ isCopyable?: boolean; wrap?: boolean }>`
	${({ theme, isCopyable }) => css`
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		flex-grow: 0;
		justify-content: start;
		padding: 0 ${theme.spacing(1)};
		position: relative;
		.MuiTypography-root {
			margin-right: ${theme.spacing(1)};
		}

		${isCopyable &&
		css`
			&:hover .copyIcon {
				opacity: 1;
				visibility: visible;
			}
		`}
	`}
`;